import { BACKEND_URL } from './config';
import { requireSession } from './supabase';
import { dispatchChatSseEvent } from './chat-sse-dispatch';
import { drainSseStream, assertSseResponse } from './sse';

/* CUTOVER (Vague 3) : SSE du labo admin sur le backend apps/api (Vertex) au lieu */
/* de la fn edge admin-chat. Mêmes events SSE (meta/delta/tool/widget/done/error). */
const ADMIN_CHAT_URL = `${BACKEND_URL}/admin/chat`;
import { listStaffThreads, listStaffMessages, type ThreadSummary, type RawWidget } from './chat-threads';

/* Client SSE du « labo d'outils » admin (edge function admin-chat). Expose tous */
/* les outils + le dessin de schéma (render_widget). Mirroir de teacherChat, + */
/* événement `widget` (HTML à rendre dans un WidgetFrame). */

export interface AdminToolCall {
  name: string;
  args?: Record<string, unknown>;
}
/** @see RawWidget — same shape, re-exported under the local name for callers. */
export type AdminWidget = RawWidget;
export interface AdminMsg {
  role: 'user' | 'assistant';
  content: string;
  failed?: boolean;
  clientSendId?: string;
  tools?: AdminToolCall[];
  widgets?: AdminWidget[];
}

/** Libellé lisible d'un appel d'outil (chip inline). */
export function adminToolLabel(t: AdminToolCall): string {
  const a = t.args ?? {};
  const keys = ['subject', 'level', 'cycle', 'voie', 'kind', 'query', 'text', 'programme_id', 'theme_id', 'table', 'limit'];
  const parts = keys.filter((k) => a[k] != null && a[k] !== '').map((k) => `${k}=${String(a[k]).slice(0, 48)}`);
  return `⚙️ ${t.name}${parts.length ? ` { ${parts.join(', ')} }` : ''}`;
}

export type AdminThread = ThreadSummary;

export async function listAdminThreads(): Promise<AdminThread[]> {
  return listStaffThreads('admin');
}

export async function listAdminMessages(threadId: string): Promise<AdminMsg[]> {
  return (await listStaffMessages(threadId)) as AdminMsg[];
}

interface StreamCallbacks {
  onDelta: (t: string) => void;
  onTool?: (name: string, args: Record<string, unknown>) => void;
  onWidget?: (w: AdminWidget) => void;
  onThread?: (threadId: string, model?: string) => void;
  onDone?: (full: string) => void;
  onError?: (e: Error) => void;
  onSlow?: () => void;
  onActive?: () => void;
}

export async function streamAdminChat(
  userMessage: string,
  threadId: string | null,
  cb: StreamCallbacks,
  signal?: AbortSignal,
  clientSendId?: string,
): Promise<void> {
  const session = await requireSession();

  const res = await fetch(ADMIN_CHAT_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ userMessage, threadId, clientSendId }),
    signal,
  });
  await assertSseResponse(res, 'admin-chat');

  await drainSseStream(res.body!, ({ event, data }) => {
    try {
      dispatchChatSseEvent(event, data, {
        onDelta: (d) => {
          if (d.text) cb.onDelta(d.text);
        },
        onTool: (d) => cb.onTool?.(d.name ?? '', (d.args as Record<string, unknown>) ?? {}),
        onWidget: (d) => {
          const w = d as { name?: string; html?: string; title?: string | null };
          if (w.html) cb.onWidget?.({ name: w.name ?? 'render_widget', html: w.html, title: w.title ?? null });
        },
        onMeta: (d) => {
          if (d.threadId) cb.onThread?.(d.threadId, d.model);
        },
        onDone: (d) => cb.onDone?.(d.text ?? ''),
        onError: (d) => cb.onError?.(new Error(d.message ?? 'unknown')),
      });
    } catch {
      /* ignore malformed frame */
    }
  }, undefined, { onSlow: cb.onSlow, onActive: cb.onActive });
}
